
import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, MessageSquare, Maximize2, Minimize2, ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { generateGrokResponse } from '@/utils/grokApiClient';
import { useNavigate } from 'react-router-dom';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I\'m your Network AI Assistant powered by S2AI with GROK enhancement. How can I help you manage your networks and devices today?',
      sender: 'assistant',
      timestamp: new Date()
    }
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Automatically scroll to the most recent message
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAutomationRequest = (text: string) => {
    // Check for automation-related requests
    const lowerText = text.toLowerCase();
    
    // Detect automation requests and navigate to AI Automation page
    if (
      (lowerText.includes('run') && lowerText.includes('automation')) ||
      (lowerText.includes('start') && lowerText.includes('automation')) ||
      lowerText.includes('optimize network') ||
      lowerText.includes('security scan') ||
      lowerText.includes('deploy new')
    ) {
      // Extract the task from the message
      let task = '';
      
      if (lowerText.includes('network')) task = 'Network Optimization';
      else if (lowerText.includes('security')) task = 'Security Audit';
      else if (lowerText.includes('deploy')) task = 'New Deployment';
      else if (lowerText.includes('backup')) task = 'Backup System';
      else task = 'Automation Task';
      
      // Navigate to AI Automation page with the task
      setTimeout(() => {
        navigate('/ai-automation', { 
          state: { 
            activateTab: 'automations',
            task: task
          } 
        });
        
        toast.info(`Navigating to AI Automation`, {
          description: `Setting up "${task}" based on your request.`
        });
      }, 1500);
      
      return true;
    }
    
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    // Check if this is an automation request
    const isAutomation = handleAutomationRequest(input);
    
    try {
      // Call GROK-powered S2AI API
      const response = await generateGrokResponse(input);
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response,
        sender: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.includes('completed') || response.includes('success')) {
        toast.success('Network task completed', {
          description: 'The requested operation was successful.'
        });
      }
    } catch (error) {
      console.error('Error generating GROK-powered response:', error);
      toast.error('Failed to connect to S2AI with GROK', {
        description: 'Please try again later.'
      });
      
      // Add error message
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error connecting to S2AI with GROK. Please try again later.',
        sender: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      scrollToBottom();
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 rounded-full h-12 w-12 p-0 shadow-lg"
        aria-label="Open AI Assistant"
      >
        <Bot className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <Card className={`fixed bottom-4 right-4 shadow-xl transition-all duration-200 border border-border ${
      isMinimized 
        ? 'w-80 h-12' 
        : 'w-80 sm:w-96 h-[450px]'
    }`}>
      <CardHeader className="p-3 border-b flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center">
          <Sparkles className="h-5 w-5 mr-2 text-primary" />
          <h3 className="font-semibold text-sm">S2AI Network Assistant</h3>
          <span className="text-xs text-muted-foreground ml-1">with GROK</span>
        </div>
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={toggleMinimize}
            aria-label={isMinimized ? "Maximize chat window" : "Minimize chat window"}
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsOpen(false)}
            aria-label="Close chat window"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      {!isMinimized && (
        <>
          <CardContent className="p-3 h-[350px] overflow-auto">
            <div className="space-y-4">
              {messages.map((message) => (
                <div 
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`rounded-lg px-3 py-2 max-w-[80%] ${
                      message.sender === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    {message.text}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>
          
          <CardFooter className="p-3 border-t">
            <form onSubmit={handleSubmit} className="flex w-full gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                className="flex-grow"
                disabled={isLoading}
                aria-label="Type a message to S2AI assistant"
              />
              <Button type="submit" size="icon" className="flex-shrink-0" disabled={isLoading} aria-label="Send message">
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </form>
          </CardFooter>
        </>
      )}
    </Card>
  );
};

export default AIAssistant;
